import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { displayToast } from "../utils/Toast";
import { useAuth } from "../context/AuthContext";

const AUTH_ERRORS = ["Token expired", "Invalid Token", "Unauthorized"];

/**
 * Custom hook for handling API errors consistently
 * @returns {Object} Error handler functions
 */
export function useErrorHandler() {
  const navigate = useNavigate();
  const { handleLogout } = useAuth();

  const handleErrorResponse = useCallback(
    (error) => {
      // Handle authentication errors
      if (AUTH_ERRORS.some((authError) => error?.includes?.(authError))) {
        displayToast("Session expired. Please re-login.", "error");
        handleLogout();
        navigate("/login", { replace: true });
        return;
      }

      // Handle other errors
      const errorMessage = typeof error === "string" 
        ? error 
        : error?.message || error?.description || "An unexpected error occurred";
      
      displayToast(errorMessage, "error");
    },
    [handleLogout, navigate]
  );

  const handleApiError = useCallback(
    (error, fallbackMessage = "An error occurred") => {
      console.error("API Error:", error);
      
      if (error?.response?.status === 401) {
        handleErrorResponse("Token expired");
        return;
      }

      const message = error?.response?.data?.message 
        || error?.message 
        || fallbackMessage;
      
      displayToast(message, "error");
    },
    [handleErrorResponse]
  );

  return { handleErrorResponse, handleApiError };
}
